import type { User } from '@/types';

class AuthService {
  private cachedUser: User | null = null;
  private authCheckPromise: Promise<User | null> | null = null;

  /**
   * Get the CSRF token from the te-csrf cookie (readable by JS)
   */
  getCSRFToken(): string | null {
    const match = document.cookie.match(/(?:^|;\s*)te-csrf=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  /**
   * Check if the user is authenticated by calling the server.
   * Results are cached to avoid excessive API calls.
   * Call invalidateCache() to force a re-check.
   */
  async isAuthenticated(): Promise<boolean> {
    const user = await this.getUser();
    return user !== null;
  }

  /**
   * Get the current user from server session.
   * Uses caching to prevent redundant API calls within the same page lifecycle.
   */
  async getUser(): Promise<User | null> {
    if (this.cachedUser) return this.cachedUser;

    // Deduplicate concurrent calls
    if (this.authCheckPromise) return this.authCheckPromise;

    this.authCheckPromise = this._fetchCurrentUser();
    try {
      const user = await this.authCheckPromise;
      this.cachedUser = user;
      return user;
    } finally {
      this.authCheckPromise = null;
    }
  }

  /**
   * Synchronous check - returns cached user without API call.
   * Returns null if no cached user (call getUser() first).
   */
  getCachedUser(): User | null {
    return this.cachedUser;
  }

  /**
   * Invalidate the cached user, forcing next getUser() to hit the API.
   */
  invalidateCache(): void {
    this.cachedUser = null;
    this.authCheckPromise = null;
  }

  /**
   * Redirect to Discord OAuth login.
   */
  login(): void {
    window.location.href = '/api/auth/discord?state=client';
  }

  /**
   * Logout: call server to clear cookies.
   * NOTE: Navigation and store cleanup are handled by authStore.logout().
   * This method only handles the server-side session teardown.
   */
  async logout(): Promise<void> {
    try {
      const csrf = this.getCSRFToken();
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {})
        }
      });
    } catch {
      // Best effort - cookies may already be expired
    }

    this.invalidateCache();
  }

  /**
   * Handle OAuth callback. With cookie auth, tokens are already in cookies.
   * We verify the session is valid by fetching the current user from the server.
   */
  async handleAuthCallback(searchParams: URLSearchParams): Promise<User | null> {
    const status = searchParams.get('status');
    if (status !== 'success') return null;

    // Verify session by fetching current user from server
    this.invalidateCache();
    const user = await this._fetchCurrentUser();
    if (user) {
      this.cachedUser = user;
      return user;
    }

    return null;
  }

  /**
   * Refresh the access token. With cookie auth, the server handles this
   * automatically using the refresh cookie.
   */
  async refreshToken(): Promise<boolean> {
    try {
      const csrf = this.getCSRFToken();
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {})
        }
      });

      if (response.ok) {
        this.invalidateCache();
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Fetch current user from the /api/auth/me endpoint.
   * On 401, attempts token refresh once before giving up.
   * @private
   */
  private async _fetchCurrentUser(): Promise<User | null> {
    try {
      let response = await fetch('/api/auth/me', {
        credentials: 'include'
      });

      // If access token expired, try to refresh it
      if (response.status === 401) {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          // Retry with new access token
          response = await fetch('/api/auth/me', {
            credentials: 'include'
          });
        }
      }

      if (!response.ok) return null;

      const data = await response.json();
      // Server returns { success, user: { userId, ... } }
      const user = data?.user ?? data;
      if (user && user.userId) {
        return user as User;
      }
      return null;
    } catch {
      return null;
    }
  }
}

export const authService = new AuthService();
